/**
 * The herbs and the gathering (M3-03).
 *
 * The ticket says the herb descriptions are "where the 'wonder' tone lives at
 * ground level", and that is the brief this file is written to. Two rules
 * follow from it, and both are worth stating because they are easy to break
 * with a well-meant edit:
 *
 * 1. **The unknown text describes what you can see; the known text tells you
 *    what it is.** Before you have picked yarrow it is "a white-flowered herb"
 *    with flat umbels and feathery leaves — everything a person standing over
 *    it could observe. After, it is the warriors' woundwort, and the sentence
 *    is allowed to carry the lore. That gap *is* the identification mechanic;
 *    there is no skill UI anywhere.
 * 2. **No numbers, and no promises.** "Staunches bleeding" is a claim the
 *    healer makes, not a tooltip. What M4-03 does with yarrow is M4-03's
 *    business, and an examine line that said "+1 die" would turn a plant into
 *    an item card.
 *
 * The five medical herbs are the Meddygon Myddfai list from the design spec.
 * The rest — berries, hazelnuts, firewood, reeds — are here because a valley
 * where the only thing worth bending down for is medicine is a valley with one
 * verb in it.
 */
import { DAYS } from '@annwn/sim';
/* -------------------------------------------------------------------------- *
 * Items
 * -------------------------------------------------------------------------- */
/**
 * Weights are in grams and are honest, which matters more than it sounds:
 * M4-06's carrying rules and M3-06's fire both want to know what a satchel
 * costs, and a herb that weighed "1 unit" would have to be re-guessed then.
 */
export const ITEMS = [
    {
        id: 'yarrow',
        name: 'yarrow',
        unknownName: 'a white-flowered herb',
        weight: 8,
        stackable: true,
        shelfLife: DAYS * 6,
        tags: ['herb', 'staunching', 'poultice'],
        examine: 'Yarrow — the warriors’ woundwort, that Achilles is said to have carried to Troy. ' +
            'Crushed to a poultice it closes a wound that will not stop.',
        unknownExamine: 'A flat white head of tiny flowers on a tough stem, the leaves so finely cut they ' +
            'look like feathers. It smells sharp and green when you break it.',
    },
    {
        id: 'willow-bark',
        name: 'willow bark',
        unknownName: 'strips of grey bark',
        weight: 20,
        stackable: true,
        // Bark keeps. That is half the point of bark.
        shelfLife: null,
        // `remedy` is the tag that makes it something a body takes *internally*
        // (M4-03) — chewed as it is, or boiled into `willow-brew` at a fire.
        tags: ['herb', 'remedy', 'pain', 'fever', 'brew'],
        examine: 'Willow bark. Chewed, or brewed bitter over a fire, it dulls a pain and takes the ' +
            'heat out of a fever. It mends nothing — it only makes the mending bearable.',
        unknownExamine: 'Strips peeled from a waterside tree, grey outside and pale within. Bitter enough ' +
            'on the tongue to make you spit.',
    },
    {
        id: 'honey',
        name: 'honey',
        unknownName: 'a comb of wild honey',
        weight: 60,
        stackable: true,
        shelfLife: null,
        tags: ['herb', 'antiseptic', 'dressing', 'food'],
        nutrition: 150,
        examine: 'Wild honey, still in the comb. Spread on a dressing it keeps a wound sweet and ' +
            'clean where nothing else will. The bees are not generous about it.',
        unknownExamine: 'A ragged piece of comb, dark with heather honey and sticky to the touch. Something ' +
            'fought to keep this.',
    },
    {
        id: 'garlic',
        name: 'garlic',
        unknownName: 'a pungent white bulb',
        weight: 15,
        stackable: true,
        shelfLife: DAYS * 30,
        tags: ['herb', 'antiseptic', 'wash', 'food'],
        nutrition: 40,
        examine: 'Garlic from the village beds. Crushed into a wash it cleans a wound of what would ' +
            'otherwise fester. Cheap, and it works, which is a rare pairing.',
        unknownExamine: 'A white bulb in a papery skin, broken into cloves. The smell stays on your hands ' +
            'for the rest of the day.',
    },
    {
        id: 'sphagnum',
        name: 'sphagnum moss',
        unknownName: 'a handful of pale bog moss',
        weight: 12,
        stackable: true,
        shelfLife: DAYS * 4,
        tags: ['herb', 'dressing', 'absorbent'],
        examine: 'Sphagnum moss, wrung out. It drinks up what a wound gives and holds it, and it ' +
            'sours less than linen does. The bog is full of it and the bog owes you nothing.',
        unknownExamine: 'Pale spongy moss, cold and heavy with water. Squeezed, it gives up more than you ' +
            'would think it held.',
    },
    /**
     * Purpose-made bandaging (M4-01). Bought or torn from a bolt of cloth, and the
     * baseline a treatment roll is measured against: a yarrow poultice reads one
     * die better, a strip off your own tunic one die worse. Consumed on use.
     */
    {
        id: 'linen',
        name: 'linen strips',
        unknownName: 'strips of clean cloth',
        weight: 20,
        stackable: true,
        shelfLife: null,
        tags: ['dressing', 'bandage'],
        examine: 'Strips of boiled linen, rolled and kept dry against the day somebody bleeds. ' +
            'Nothing miraculous — only clean, and clean is most of what a dressing is for.',
        unknownExamine: 'Torn lengths of pale cloth, washed soft and rolled tight. Somebody meant these for ' +
            'a wound before they had one.',
    },
    /* -------------------------------------------------------------------------- *
     * Prepared at a fire (M4-03)
     * -------------------------------------------------------------------------- */
    /**
     * Yarrow, water and cloth, worked together over a fire.
     *
     * Note the shelf life against the six days raw yarrow keeps: a poultice is
     * better in every way that matters for two days and then it is nothing. That
     * gap is the whole argument of a preparation — you make these the evening
     * before you expect trouble, not the month before.
     */
    {
        id: 'yarrow-poultice',
        name: 'a yarrow poultice',
        unknownName: 'a poultice of crushed leaves',
        weight: 30,
        stackable: true,
        shelfLife: DAYS * 2,
        tags: ['herb', 'poultice', 'dressing', 'prepared'],
        examine: 'Yarrow crushed to a green paste and worked into a strip of linen, still damp. It ' +
            'will hold a wound shut better than anything you can pick whole — while it lasts.',
        unknownExamine: 'A strip of cloth with something green and wet crushed into it. It smells sharp, and ' +
            'somebody meant it for a wound.',
    },
    {
        id: 'willow-brew',
        name: 'a willow brew',
        unknownName: 'a flask of dark liquor',
        weight: 100,
        stackable: true,
        shelfLife: DAYS * 1,
        tags: ['herb', 'remedy', 'prepared', 'brew'],
        examine: 'Willow bark boiled down bitter. Drunk hot it dulls a pain and takes the heat out of ' +
            'a fever for half a day. It does not mend anything, and it does not pretend to.',
        unknownExamine: 'Dark liquor gone cloudy, bitter enough to make your eyes water from a foot away. ' +
            'One mouthful would be an act of faith.',
    },
    /**
     * The needle a suture needs (M4-01/02). A tool, not a supply — it is not used
     * up, but a wound only closes with one to hand and a steady hand to hold it.
     */
    {
        id: 'needle-and-thread',
        name: 'a bone needle and thread',
        unknownName: 'a bone needle and thread',
        weight: 10,
        stackable: false,
        shelfLife: null,
        tags: ['tool', 'suture'],
        examine: 'A needle worked from bone, and a hank of waxed sinew wound round it. It closes a ' +
            'clean cut so it heals in a week instead of a fortnight — if the hand that drives it ' +
            'is steady enough.',
        // No identification gap: a needle is a needle, whoever is holding it.
        unknownExamine: 'A needle worked from bone, and a hank of waxed sinew wound round it. It closes a ' +
            'clean cut so it heals in a week instead of a fortnight — if the hand that drives it ' +
            'is steady enough.',
    },
    /* -------------------------------------------------------------------------- *
     * Not medicine
     * -------------------------------------------------------------------------- */
    {
        id: 'berries',
        name: 'bilberries',
        unknownName: 'small dark berries',
        weight: 5,
        stackable: true,
        shelfLife: DAYS * 3,
        tags: ['food', 'forage'],
        nutrition: 120,
        examine: 'Bilberries — whinberries, if you are from here. A handful is a mouthful and stains ' +
            'it black. Nobody ever got through a winter on them, but they are not nothing.',
        unknownExamine: 'Small dark berries with a dusty bloom on them, low down among the leaves. They give ' +
            'under a thumb.',
    },
    {
        id: 'hazelnuts',
        name: 'hazelnuts',
        unknownName: 'nuts in the husk',
        weight: 10,
        stackable: true,
        shelfLife: null,
        tags: ['food', 'forage', 'roastable'],
        nutrition: 200,
        examine: 'Hazelnuts. They keep all winter in a dry place, which is more than can be said for ' +
            'anything else the wood gives up. In the old stories, eating them makes you wise.',
        unknownExamine: 'Hard nuts in ragged green husks, three or four to a cluster. Heavy for their size.',
    },
    {
        id: 'roasted-hazelnuts',
        name: 'roasted hazelnuts',
        unknownName: 'warm cracked nuts',
        weight: 8,
        stackable: true,
        shelfLife: null,
        tags: ['food', 'cooked'],
        nutrition: 240,
        examine: 'Hazelnuts roasted until their skins split. Warm, sweet, and worth the little fire they took.',
        unknownExamine: 'Warm nuts with their skins cracked dark from the fire.',
    },
    {
        id: 'firewood',
        name: 'firewood',
        unknownName: 'a bundle of dead wood',
        weight: 400,
        stackable: true,
        shelfLife: null,
        tags: ['fuel', 'forage'],
        examine: 'Dead wood, gathered and bundled. Dry enough to catch, which is the only thing that ' +
            'will matter about it on the wrong night.',
        unknownExamine: 'Fallen branches, snapped to length and tied. Heavy on the shoulder.',
    },
    /* -------------------------------------------------------------------------- *
     * M3-06 — the camp
     * -------------------------------------------------------------------------- */
    /**
     * What comes off a carcass, and the reason to hunt something bigger than a
     * rabbit.
     *
     * Note the shelf life: three days raw against twelve cooked. That gap is the
     * whole argument for a fire — cooking is not a small bonus on a meal, it is
     * what turns a stag into a week's food instead of an afternoon's.
     */
    {
        id: 'raw-meat',
        name: 'raw meat',
        unknownName: 'a cut of raw meat',
        weight: 500,
        stackable: true,
        shelfLife: DAYS * 3,
        tags: ['food', 'raw', 'meat'],
        nutrition: 350,
        examine: 'A cut of meat, dark and wet, still smelling of the animal. It will feed you as it is, ' +
            'and about a third of the time it will make you wish it had not.',
        unknownExamine: 'Meat, uncooked. Heavy, and it drips.',
    },
    {
        id: 'cooked-meat',
        name: 'cooked meat',
        unknownName: 'a cut of cooked meat',
        weight: 420,
        stackable: true,
        shelfLife: DAYS * 12,
        tags: ['food', 'cooked', 'meat'],
        nutrition: 500,
        examine: 'Meat blackened on the outside and hot through. It keeps for days now, and it will not ' +
            'turn on you. This is what a fire is for.',
        unknownExamine: 'Cooked meat, charred at the edges. It smells like the best thing in the world.',
    },
    /**
     * Struck steel and a stone. Never spent, and it halves how often the tinder
     * refuses — which is 40 grams well carried in a wet week.
     */
    {
        id: 'flint',
        name: 'a flint and steel',
        unknownName: 'a flint and steel',
        weight: 40,
        stackable: false,
        shelfLife: null,
        tags: ['flint', 'tool'],
        examine: 'A wedge of flint and a curved strike of steel, wrapped together in oiled cloth with a ' +
            'little charred linen. It will get a light going on a night when nothing else would.',
        // Nobody needs teaching what a flint is: there is no identification gap to
        // open, so the two examines are the same sentence.
        unknownExamine: 'A wedge of flint and a curved strike of steel, wrapped together in oiled cloth with a ' +
            'little charred linen. It will get a light going on a night when nothing else would.',
    },
    /**
     * A scrap of tinder — the least you can start a fire with (RC2-04).
     *
     * Light enough to be nothing on the shoulder, tagged `fuel` so `build` will
     * take it: it is the "almost nothing" the reward ladder starts you with, the
     * thing that makes a fire *possible* on the first night rather than a thing
     * you have to earn first. A handful of dry moss and shavings, not a bundle of
     * logs — the firewood you gather; the tinder you were handed.
     */
    {
        id: 'tinder',
        name: 'a scrap of tinder',
        unknownName: 'a twist of dry moss and shavings',
        weight: 15,
        stackable: true,
        shelfLife: null,
        tags: ['fuel', 'tinder'],
        examine: 'A twist of dry moss, birch bark and fine shavings, kept in a fold of cloth against the ' +
            'wet. Not a fire — the makings of one, and the difference between a spark taking and a ' +
            'cold night wishing it had.',
        unknownExamine: 'A twist of dry moss and shavings, kept carefully out of the wet.',
    },
    /**
     * The axe — the tool the reward ladder is built around (RC2-04).
     *
     * A satchel tool, not the combat weapon of the same name (`weapons.ts` keeps
     * the bearded axe you *fight* with; this is the one you *fell* with): a
     * background trades with the weapon it arrived holding, and the found axe opens
     * a door — woodcraft — rather than replacing that. Never spent, like the flint
     * and the needle. The `felling` tag is what `chop` reads and what the sheet's
     * woodcraft door asks after; nothing starts the game carrying it, which is the
     * whole point — getting it is meant to be an event.
     */
    {
        id: 'axe',
        name: 'a felling axe',
        unknownName: 'an axe, half-buried',
        weight: 1200,
        stackable: false,
        shelfLife: null,
        tags: ['tool', 'axe', 'felling'],
        examine: 'A working axe, the haft worn smooth and pale where a hand has held it and the head still ' +
            'keen under the rust. Whoever left it in the deadfall is not coming back for it. It will ' +
            'bring down wood no knife ever could — and a craft comes with it, if you have the arm.',
        // A found axe is an axe: no identification gap worth a second sentence.
        unknownExamine: 'The head of an axe, half-buried in the fallen hazel, one bright edge catching the light.',
    },
    {
        id: 'knife',
        name: 'a hearth knife',
        unknownName: 'an antler-handled knife',
        weight: 180,
        stackable: false,
        shelfLife: null,
        tags: ['tool', 'knife'],
        examine: 'A plain working knife with an antler handle: enough edge for dressing food, cutting cord ' +
            'and defending yourself when something comes too close.',
        unknownExamine: 'A short iron blade set into a worn antler handle.',
    },
    {
        id: 'bow',
        name: 'a yew bow',
        unknownName: 'a bow wrapped in oilcloth',
        weight: 700,
        stackable: false,
        shelfLife: null,
        tags: ['tool', 'bow'],
        examine: 'A yew bow, dark at the grip and kept dry under oilcloth. Six plain ash arrows rest beside it.',
        unknownExamine: 'Oilcloth slips from a bent stave and a small, fletched bundle.',
    },
    {
        id: 'reeds',
        name: 'reeds',
        unknownName: 'a sheaf of cut reeds',
        weight: 30,
        stackable: true,
        shelfLife: null,
        tags: ['material', 'forage', 'thatch'],
        examine: 'Reeds, cut green and left to dry. Thatch, bedding, a floor worth walking on — the ' +
            'marsh gives the valley half of what it is built from.',
        unknownExamine: 'Tall stems cut off at the water, still smelling of the mud they came out of.',
    },
    /* -------------------------------------------------------------------------- *
     * After the surgery (M4-05)
     * -------------------------------------------------------------------------- */
    /**
     * What came out of the wound.
     *
     * Weightless to within a gram, worth nothing, and the item this milestone is
     * most confident testers will keep. Its provenance is written onto the stack
     * as it is dropped into the satchel (`ItemStack.note`), so it does not say "a
     * splinter of bone" but "the tusk that went into Gwil's thigh at Bryn Tywyll"
     * — which is the difference between loot and a keepsake.
     */
    {
        id: 'fragment',
        name: 'a splinter',
        unknownName: 'a splinter',
        weight: 5,
        stackable: false,
        shelfLife: null,
        tags: ['keepsake'],
        examine: 'A shard of bone or iron, dark at one end where it sat in somebody. Whoever dug it ' +
            'out kept it, and whoever it came out of will want to see it.',
        unknownExamine: 'A shard of bone or iron, dark at one end where it sat in somebody. Whoever dug it ' +
            'out kept it, and whoever it came out of will want to see it.',
    },
    /**
     * Two hours with a knife and a forked branch, and a man is on his feet again.
     *
     * `mobility` is what leaning on it is worth (M4-05): 0.35 against the 0.25 of
     * crawling, and against the 0.45 of the smith's peg-leg. Carved rather than
     * bought, because the first amputation in the valley will happen a long way
     * from anybody who could sell you anything.
     */
    {
        id: 'crutch',
        name: 'a crutch',
        unknownName: 'a forked branch, cut short',
        weight: 900,
        stackable: false,
        shelfLife: null,
        tags: ['mobility', 'tool'],
        mobility: 0.35,
        examine: 'A forked ash branch cut to the armpit and padded with a wad of fleece. It is not ' +
            'walking, but it is not crawling either.',
        unknownExamine: 'A forked branch, cut short and bound with fleece at the top. Somebody meant it for ' +
            'somebody.',
    },
    /**
     * The smith's answer, and better (M5-01 sells it; it exists here so that the
     * ladder crutch → peg-leg is real the day the trade lands rather than a
     * promise in a comment).
     */
    {
        id: 'peg-leg',
        name: 'a peg-leg',
        unknownName: 'a turned wooden leg',
        weight: 1400,
        stackable: false,
        shelfLife: null,
        tags: ['mobility', 'tool'],
        mobility: 0.45,
        examine: 'Ash turned round, socketed in boiled leather and strapped above the knee. The smith ' +
            'made the fittings and swore about it the whole time.',
        unknownExamine: 'A turned wooden leg with leather straps and iron fittings at the top.',
    },
    /* ---------------------------------------------------------------------- *
     * What comes off an animal besides the meat (M5-01)
     * ---------------------------------------------------------------------- */
    /**
     * The four goods the valley's economy actually runs on.
     *
     * They exist for one reason and it is worth stating plainly: **meat spoils
     * and hides do not.** A hunter who kills a stag on the third day out gets
     * home with rotten meat and a perfectly good skin, and if the skin were worth
     * nothing then hunting further than an afternoon from the village would never
     * pay. `shelfLife: null` on all four is the whole trade economy in one field.
     *
     * They are also deliberately *not* armour. A stag hide worn by a stag is
     * `stag.hide` in the armour table; this is the same animal's skin as a bale
     * on your back, and turning one into the other is a tanner's job the MVP does
     * not have. What it has is a smith who will take it off you.
     */
    {
        id: 'hide',
        name: 'a hide',
        unknownName: 'a green hide',
        weight: 3000,
        stackable: true,
        shelfLife: null,
        tags: ['material', 'hide', 'trade'],
        examine: 'A skin off something big, scraped on the flesh side and rolled hair-out. It weighs ' +
            'like a wet sack and it is worth carrying anyway.',
        unknownExamine: 'A rolled skin, heavy and still smelling of the animal.',
    },
    {
        id: 'pelt',
        name: 'a pelt',
        unknownName: 'a small skin',
        weight: 900,
        stackable: true,
        shelfLife: null,
        tags: ['material', 'hide', 'trade'],
        examine: 'A wolf skin, or something near enough. Not enough of it to make anything, and ' +
            'enough of it that a man in the village will take it off your hands.',
        unknownExamine: 'A small skin, rolled and tied.',
    },
    {
        id: 'fleece',
        name: 'a fleece',
        unknownName: 'a bundle of wool',
        weight: 1800,
        stackable: true,
        shelfLife: null,
        tags: ['material', 'wool', 'trade'],
        examine: 'A whole fleece rolled cut-side in, greasy with lanolin and smelling strongly of ' +
            'sheep. This is what the valley wears, when the valley is not wearing leather.',
        unknownExamine: 'A rolled bundle of oily wool, tied with its own twist.',
    },
    {
        id: 'tusks',
        name: 'a pair of tusks',
        unknownName: 'two curved teeth',
        weight: 400,
        stackable: true,
        shelfLife: null,
        // `trophy` (M5-05): a pair the hunt is remembered by comes off the block
        // with a provenance note. Being noted, it never merges into an anonymous
        // heap, so a mounted pair can still name the day it was taken.
        tags: ['material', 'ivory', 'trade', 'trophy'],
        examine: 'Two curves of ivory, worn to a chisel edge where they ran against the uppers. ' +
            'Everybody who sees them will ask, and that is most of what they are worth.',
        unknownExamine: 'Two curved teeth, heavier than they look.',
    },
    /**
     * The smith's whetstone — the only thing on his stall that is not steel.
     *
     * It is here because a party three days out with a `blunted` seax otherwise
     * has to walk home to swing properly again, and "walk home" is not a
     * decision, it is an interruption. Buying one is the moment a group stops
     * being visitors to the wilderness and starts being able to stay in it.
     */
    /**
     * The old spearheads out of Ysgithyrwyn's hide (M5-04).
     *
     * Three of them, driven in by three hunters the valley still half-remembers,
     * and carried in his shoulders for the years it took each haft to rot away
     * and drop. They come off him at the block, one by one, and each one is its
     * own item because each one is its own dead man — the whole of what "the boar
     * that killed the men who went before" means, held in the hand at last.
     *
     * They are worth almost nothing and mean a great deal, which is the correct
     * ratio for a relic. The smith will not price them; the hall will hang them by
     * the tusks (M5-05).
     */
    {
        id: 'spearhead.cadog',
        name: "Cadog's spearhead",
        unknownName: 'a rusted spearhead',
        weight: 200,
        stackable: false,
        shelfLife: null,
        tags: ['material', 'relic', 'trophy'],
        examine: 'A leaf-bladed spearhead, the iron eaten to lace with rust and the socket ' +
            'still holding a plug of rotted ash. Old Cadog carried a spear like this up ' +
            'the ford the winter before you were born, and did not carry it back.',
        unknownExamine: 'A rusted spearhead, the wood long gone from its socket.',
    },
    {
        id: 'spearhead.maelan',
        name: "Maelan's spearhead",
        unknownName: 'a rusted spearhead',
        weight: 200,
        stackable: false,
        shelfLife: null,
        tags: ['material', 'relic', 'trophy'],
        examine: 'A narrow, wicked point, bent a little where it turned on bone. They say ' +
            'Maelan of the mill went out alone against the wisdom of everyone and was ' +
            'three days in the finding. This is the piece of him the boar kept.',
        unknownExamine: 'A rusted spearhead, its point turned aside on something hard.',
    },
    {
        id: 'spearhead.unknown',
        name: 'a nameless spearhead',
        unknownName: 'a rusted spearhead',
        weight: 200,
        stackable: false,
        shelfLife: null,
        tags: ['material', 'relic', 'trophy'],
        examine: 'A broad old blade of a make nobody in the valley forges any more, worn ' +
            'thin as a leaf. Whoever set it in him is further back than the names go. ' +
            'He has been the death of this place for a long time.',
        unknownExamine: 'A rusted spearhead of an old, broad make.',
    },
    {
        id: 'whetstone',
        name: 'a whetstone',
        unknownName: 'a bar of grey stone',
        weight: 300,
        stackable: false,
        shelfLife: null,
        tags: ['tool', 'whetstone'],
        examine: 'A bar of close grey stone worn hollow in the middle, on a thong. Spit on it and ' +
            'work the edge back, and do it before you need to rather than after.',
        unknownExamine: 'A bar of grey stone, worn hollow in the middle, on a leather thong.',
    },
];
const ITEM_INDEX = new Map(ITEMS.map((item) => [item.id, item]));
export const ITEM_KIT = ITEM_INDEX;
export function item(id) {
    const found = ITEM_INDEX.get(id);
    if (!found)
        throw new Error(`no item "${id}"`);
    return found;
}
/* -------------------------------------------------------------------------- *
 * Flora — what grows where
 * -------------------------------------------------------------------------- */
/** A game day, so the regrowth numbers below read as the durations they are. */
const DAY = DAYS;
/**
 * Habitat weights are *relative*, not probabilities: worldgen scales the whole
 * table to a target density, so doubling every number here changes nothing and
 * doubling one changes where a plant is found.
 *
 * The moisture band is what makes these rules teach something. Yarrow and
 * bilberry both grow on open ground, and you will not find them in the same
 * place, because one wants it dry and the other does not — which is true of
 * the actual hillside, and is the transferable knowledge M3-03 is after.
 */
export const FLORA = [
    {
        id: 'yarrow',
        name: 'a stand of yarrow',
        itemId: 'yarrow',
        habitat: { meadow: 1, grass: 1.4, ffridd: 0.5, path: 0.2, mound: 0.6 },
        moisture: { ideal: 0.3, tolerance: 0.28 },
        picks: 3,
        regrowTicks: DAY * 2,
        yield: 2,
    },
    {
        id: 'willow',
        name: 'a willow, low over the water',
        itemId: 'willow-bark',
        // "Riverbank" has no habitat token, because a bank is whatever biome
        // happens to border the water. High moisture is the proxy — worldgen's
        // moisture field already folds in distance to water — and the terrain
        // weights only keep it out of places a tree cannot stand. A real
        // `nearWater` axis would say it better and is worth adding the day
        // something else needs one.
        habitat: { meadow: 0.6, saltmarsh: 1, bog: 0.5, oakwood: 0.15, reeds: 1.4 },
        moisture: { ideal: 0.8, tolerance: 0.26 },
        picks: 4,
        // Bark grows back slowly, and a tree you have stripped shows it.
        regrowTicks: DAY * 5,
        yield: 2,
    },
    {
        id: 'hive',
        name: 'a wild bees’ nest',
        itemId: 'honey',
        habitat: { oakwood: 1, hazelwood: 0.5 },
        moisture: { ideal: 0.55, tolerance: 0.3 },
        picks: 2,
        regrowTicks: DAY * 6,
        yield: 1,
    },
    {
        id: 'garlic',
        name: 'a bed of garlic',
        itemId: 'garlic',
        // Village-grown, so it wants the trodden ground people live on. Worldgen's
        // site pass is what actually puts `dirt` and `floor` in the valley, so this
        // finds the village without knowing where the village is.
        habitat: { dirt: 3, floor: 1 },
        moisture: { ideal: 0.4, tolerance: 0.45 },
        picks: 3,
        regrowTicks: DAY * 3,
        yield: 2,
    },
    {
        id: 'sphagnum',
        name: 'a cushion of bog moss',
        itemId: 'sphagnum',
        habitat: { bog: 2.2, saltmarsh: 0.8, moor: 0.2 },
        moisture: { ideal: 0.8, tolerance: 0.3 },
        picks: 4,
        regrowTicks: DAY * 2,
        yield: 3,
    },
    {
        id: 'bilberry',
        name: 'bilberry, low among the heather',
        itemId: 'berries',
        habitat: { moor: 1, ffridd: 0.7, hazelwood: 0.3 },
        moisture: { ideal: 0.6, tolerance: 0.3 },
        picks: 3,
        regrowTicks: DAY * 3,
        yield: 4,
    },
    {
        id: 'hazel',
        name: 'a hazel heavy with nuts',
        itemId: 'hazelnuts',
        habitat: { hazelwood: 1.4, oakwood: 0.4, ffridd: 0.3 },
        moisture: { ideal: 0.5, tolerance: 0.35 },
        picks: 3,
        regrowTicks: DAY * 4,
        yield: 3,
    },
    {
        id: 'deadwood',
        name: 'fallen wood',
        itemId: 'firewood',
        habitat: { oakwood: 1, hazelwood: 0.8, ffridd: 0.3 },
        moisture: { ideal: 0.45, tolerance: 0.45 },
        picks: 2,
        regrowTicks: DAY * 2,
        yield: 1,
    },
    {
        id: 'reedbed',
        name: 'a reedbed',
        itemId: 'reeds',
        habitat: { reeds: 4, saltmarsh: 0.9 },
        moisture: { ideal: 0.75, tolerance: 0.35 },
        picks: 4,
        regrowTicks: DAY * 3,
        yield: 2,
    },
];
const FLORA_INDEX = new Map(FLORA.map((def) => [def.id, def]));
export const FLORA_KIT = FLORA_INDEX;
export function flora(id) {
    const found = FLORA_INDEX.get(id);
    if (!found)
        throw new Error(`no flora "${id}"`);
    return found;
}
/**
 * Every species that names an item, checked at load.
 *
 * A flora record pointing at an item that does not exist is a patch a player
 * can stand on, forage, and receive nothing from — which would look like a
 * bug in the forage verb rather than a typo in a data file three packages
 * away. Cheap to check here, miserable to diagnose there.
 */
function checkFlora() {
    for (const def of FLORA) {
        if (!ITEM_INDEX.has(def.itemId)) {
            throw new Error(`flora "${def.id}" yields "${def.itemId}", which is not an item`);
        }
        if (Object.keys(def.habitat).length === 0) {
            throw new Error(`flora "${def.id}" grows nowhere`);
        }
    }
}
checkFlora();
//# sourceMappingURL=flora.js.map